import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Container, SectionHeading } from "@/components/common/ui";
import { LazyMount } from "@/components/common/LazyMount";
import { BaseExplorer } from "@/components/bases/BaseExplorer";
import { Hero } from "@/components/home/Hero";
import { StatsBand } from "@/components/home/StatsBand";
import { FeaturedHomes } from "@/components/home/FeaturedHomes";
import { AudiencePaths, LifestyleTiles } from "@/components/home/Tiles";
import { TestimonialCarousel } from "@/components/home/TestimonialCarousel";
import { PcsCta, SellerBand, TeamQuote } from "@/components/home/Sections";
import { MEDIA, VideoBand } from "@/components/media/BrandVideo";
import { GuideTiles } from "@/components/guides/GuideTiles";
import { HomeValueBand } from "@/components/guides/HomeValueBand";

const ExplorerSection = () => (
  <section id="explorer" className="scroll-mt-16 py-16 sm:py-20" data-testid="home-explorer-section">
    <Container>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <SectionHeading eyebrow="Installation explorer" title="Every base and shipyard, in 3D"
          subtitle="Fly into each installation, see the homes around it, and know exactly how far your commute is before you ever visit." />
        <Link to="/bases" className="hidden text-[15px] text-ink underline underline-offset-4 hover:decoration-brass lg:inline" data-testid="home-explorer-fullscreen">Open full screen</Link>
        <Link to="/bases" data-testid="home-explorer-open-mobile"
          className="inline-flex w-fit items-center gap-2 rounded-full bg-ink px-5 py-3 text-[14px] font-medium text-white transition-colors hover:bg-neutral-800 lg:hidden">
          Open the 3D base explorer <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </Container>
    {/* The embedded 3D map is desktop-only; phones and tablets open it on its own page via the button above. */}
    <div className="mx-auto mt-8 hidden max-w-[1440px] px-8 lg:block">
      <LazyMount className="h-[720px] overflow-hidden rounded-md">
        <BaseExplorer className="h-full" variant="section" testId="home-base-explorer" />
      </LazyMount>
    </div>
  </section>
);

const IntroCopy = () => (
  <section className="border-t border-neutral-200 py-16" data-testid="intro-copy">
    <Container className="grid gap-8 lg:grid-cols-[1fr_1.4fr]">
      <h2 className="text-2xl font-normal sm:text-3xl">Greater Hampton Roads and Greater Richmond Real Estate</h2>
      <div className="space-y-4 text-[16px] leading-relaxed text-neutral-600">
        <p><strong className="font-medium text-ink">Welcome to 757Realty.com, your one-stop source for Greater Hampton Roads and Greater Richmond Real Estate.</strong></p>
        <p>
          Use our <Link to="/search" className="text-ink underline underline-offset-4">interactive map search</Link> to find homes for sale in Greater Hampton Roads and Greater Richmond,
          filter by lifestyle, and see how far every home is from each military installation. Or explore the{" "}
          <Link to="/bases" className="text-ink underline underline-offset-4">bases in 3D</Link> to get a feel for the neighborhoods around your next duty station.
        </p>
        <p>
          Save your favorite homes on this device for quick and easy access whenever you need, and{" "}
          <Link to="/contact" className="text-ink underline underline-offset-4">talk to us</Link> about anything along the way. Interested in a career in real estate?{" "}
          <Link to="/contact?kind=career" className="text-ink underline underline-offset-4">Learn why The Rob Resh Team is the right choice for career-minded agents.</Link>
        </p>
        <p>Thanks for visiting 757Realty.com, home of exquisite properties and excellent service.</p>
      </div>
    </Container>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <ExplorerSection />
      <HomeValueBand />
      <GuideTiles />
      <StatsBand />
      <FeaturedHomes />
      <AudiencePaths />
      <SellerBand />
      <VideoBand media={MEDIA.cover} testId="home-brand-film" caption="757 Realty & 804 Homes · Real estate, simplified"
        className="mt-16 sm:mt-24" frameClass="aspect-[1600/608]" />
      <TeamQuote />
      <LifestyleTiles />
      <TestimonialCarousel />
      <PcsCta />
      <IntroCopy />
    </div>
  );
}
